/* -------------------------------------------------------------------------- */
/* CAMERA SCENE ACTIONS                                                       */
/* -------------------------------------------------------------------------- */
/*
 * Cloud Scenes camera actions are written to the write-only IoTS tokens below.
 * The scene backend translates `{ r: 'dev:<id>:<token>', set: { action, params } }`
 * into an RPC call `<component>.<action>` with `params`:
 *   - camera_action      -> camera.<action>
 *   - camerazone_action  -> camerazone.<action>
 *
 */

import {
	CAMERA_DURATION_MIN,
	CAMERA_DURATION_MAX,
	CAMERA_DURATION_DEFAULT,
} from 'src/helpers/actions/local/devices/camera_local_actions';
import {
	convertCameraUiValueToSceneValue,
	normalizeCameraConditionValue,
} from 'src/components/Scenes/SceneWizard/Process/When/AddEditContionWizard/Steps/DeviceBased/helpers/camera-scene-helpers';

export type CameraActionName = (typeof CAMERA_ACTION_NAMES)[keyof typeof CAMERA_ACTION_NAMES];

export type CameraActionSet = {
	_isdata: true;
	action: string;
	params: Record<string, unknown>;
};

export type CameraActionPayload = {
	token: string;
	set: CameraActionSet;
};

type BuildCameraActionOptions = {
	channel?: number | string;
	zoneId?: number | string;
	duration?: number | string | null;
};

export type CameraActionParseResult = {
	name: CameraActionName;
	value: string;
	channel: number;
	add_info_ind?: string;
	duration?: number;
};

export const CAMERA_ACTION_TOKENS = {
	CAMERA: 'camera_action',
	ZONE: 'camerazone_action',
} as const;

/**
 * UI-level action names. These are matched by the action label/i18n switches and
 * by the camera action wizard component; they never reach the device directly.
 */
export const CAMERA_ACTION_NAMES = {
	CAPTURE: 'camera_capture',
	RECORD: 'camera_record',
	ARM: 'camera_arm',
	PRIVACY: 'camera_privacy',
	ZONE: 'camera_zone',
} as const;

/**
 * StartRecording duration bounds (seconds). Reuses the local-actions constants
 * (`CAMERA_DURATION_*`) so the scenes UI and local actions stay in sync.
 */
export const CAMERA_RECORD_DURATION = {
	MIN: CAMERA_DURATION_MIN,
	MAX: CAMERA_DURATION_MAX,
	DEFAULT: CAMERA_DURATION_DEFAULT,
} as const;

/**
 * Build the scene `{ token, set }` payload for a UI camera action.
 * `set` follows the object shape `{ _isdata, action, params }` consumed by the backend.
 */
export const buildCameraActionSet = (
	name: string,
	value: unknown,
	opts: BuildCameraActionOptions = {},
): CameraActionPayload | null => {
	const channelId = Number(opts.channel ?? 0) || 0;

	switch (name) {
		case CAMERA_ACTION_NAMES.CAPTURE:
			return {
				token: CAMERA_ACTION_TOKENS.CAMERA,
				set: {
					_isdata: true,
					action: 'CaptureImage',
					params: { id: channelId, stream: 0 },
				},
			};

		case CAMERA_ACTION_NAMES.RECORD: {
			const params: Record<string, unknown> = { id: channelId };
			const duration = Number(opts.duration);
			if (opts.duration != null && Number.isFinite(duration) && duration > 0) {
				params.duration = duration;
			}
			return {
				token: CAMERA_ACTION_TOKENS.CAMERA,
				set: { _isdata: true, action: 'StartRecording', params },
			};
		}

		case CAMERA_ACTION_NAMES.ARM:
			return {
				token: CAMERA_ACTION_TOKENS.CAMERA,
				set: {
					_isdata: true,
					action: 'SetConfig',
					params: {
						id: channelId,
						config: { arm: convertCameraUiValueToSceneValue(String(value)) },
					},
				},
			};

		case CAMERA_ACTION_NAMES.PRIVACY:
			return {
				token: CAMERA_ACTION_TOKENS.CAMERA,
				set: {
					_isdata: true,
					action: 'SetConfig',
					params: {
						id: channelId,
						config: { privacy: convertCameraUiValueToSceneValue(String(value)) },
					},
				},
			};

		case CAMERA_ACTION_NAMES.ZONE:
			return {
				token: CAMERA_ACTION_TOKENS.ZONE,
				set: {
					_isdata: true,
					action: 'SetConfig',
					params: {
						id: Number(opts.zoneId ?? 0) || 0,
						config: { enable: convertCameraUiValueToSceneValue(String(value)) },
					},
				},
			};

		default:
			return null;
	}
};

/**
 * Whether a scene command token is a camera action token (write-only).
 */
export const isCameraSceneActionCommand = (token: string): boolean =>
	token === CAMERA_ACTION_TOKENS.CAMERA || token === CAMERA_ACTION_TOKENS.ZONE;

/**
 * Whether a UI action name belongs to the camera action set.
 */
export const isCameraActionName = (name: string): boolean =>
	(Object.values(CAMERA_ACTION_NAMES) as string[]).includes(name);

/**
 * Reverse of {@link buildCameraActionSet}: turn a scene `{ token, set }` back into UI fields.
 */
export const parseCameraSceneActionCommand = (
	token: string,
	set: unknown,
): CameraActionParseResult | null => {
	if (!set || typeof set !== 'object') {
		return null;
	}

	const data = set as { action?: string; params?: Record<string, any> };
	const action = String(data.action || '').toLowerCase();
	const params = data.params || {};

	if (token === CAMERA_ACTION_TOKENS.CAMERA) {
		if (action === 'captureimage') {
			return {
				name: CAMERA_ACTION_NAMES.CAPTURE,
				value: 'capture',
				channel: Number(params.id ?? 0) || 0,
			};
		}

		if (action === 'startrecording') {
			return {
				name: CAMERA_ACTION_NAMES.RECORD,
				value: 'record',
				channel: Number(params.id ?? 0) || 0,
				...(params.duration != null && { duration: Number(params.duration) }),
			};
		}

		if (action === 'setconfig') {
			const config = params.config || {};
			if (typeof config.arm !== 'undefined') {
				return {
					name: CAMERA_ACTION_NAMES.ARM,
					value: normalizeCameraConditionValue(config.arm),
					channel: Number(params.id ?? 0) || 0,
				};
			}
			if (typeof config.privacy !== 'undefined') {
				return {
					name: CAMERA_ACTION_NAMES.PRIVACY,
					value: normalizeCameraConditionValue(config.privacy),
					channel: Number(params.id ?? 0) || 0,
				};
			}
		}

		return null;
	}

	if (token === CAMERA_ACTION_TOKENS.ZONE) {
		const config = params.config || {};
		return {
			name: CAMERA_ACTION_NAMES.ZONE,
			value: normalizeCameraConditionValue(config.enable),
			channel: 0,
			add_info_ind: String(params.id ?? 0),
		};
	}

	return null;
};
